// src/components/Header/styles.ts
import styled from "styled-components";
import { Link } from "react-router-dom";
import { MenuOutlined } from "@ant-design/icons";

export const HeaderSection = styled("header")`
  padding: 1rem 0.5rem;
  background: var(--primary-color, #f9c0f544);
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
  overflow: visible; /* Critical: Allow dropdown to show outside header */

  .ant-row-space-between {
    align-items: center;
    text-align: center;
  }
  
  /* Ensure container allows overflow */
  & > div {
    overflow: visible !important;
  }
  
  /* Add a slight text shadow to make text more readable on the background */
  a, span {
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

export const LogoContainer = styled(Link)`
  display: flex;
`;

export const NavLink = styled("div")`
  display: inline-block;
  text-align: center;
`;

export const CustomNavLink = styled("div")`
  width: 203px;
  display: inline-block;

  @media only screen and (max-width: 411px) {
    width: 150px;
  }

  @media only screen and (max-width: 320px) {
    width: 118px;
  }
`;

export const LaunchButton = styled("a")`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px 24px;
  background-color: var(--primary-color, #f9c0f5);
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  &:hover {
    color: white;
  }
`;

export const Burger = styled("div")`
  @media only screen and (min-width: 891px) {
    display: none;
  }

  display: block;
  cursor: pointer;
  
  .menu-button-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
  }
  
  .menu-button-horizontal-line {
    width: 24px;
    height: 2px;
    background-color: #2e186a;
    margin: 5px 0;
    transition: all 0.3s ease;
  }
`;

export const NotHidden = styled("div")`
  @media only screen and (max-width: 890px) {
    display: none;
  }
  
  /* Critical: Allow overflow for dropdown */
  overflow: visible;
`;

export const Menu = styled("h5")`
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
`;

export const CustomNavLinkSmall = styled(NavLink)`
  font-size: 1.2rem;
  color: #18216d;
  transition: color 0.2s ease-in;
  margin: 0.5rem 2rem;
  position: relative; /* Important for dropdown positioning */

  @media only screen and (max-width: 768px) {
    margin: 1.25rem 2rem;
  }
`;

export const Label = styled("span")`
  font-weight: 500;
  color: #404041;
  text-align: right;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

export const Outline = styled(MenuOutlined)`
  font-size: 22px;
`;

export const Span = styled("span")`
  cursor: pointer;
  transition: all 0.3s ease-in-out;

  &:hover,
  &:active,
  &:focus {
    color: var(--primary-color, #f9c0f5);
    text-underline-position: under;
    text-decoration: var(--primary-color, #f9c0f5) underline;
  }
`;

// Dropdown Styles
export const DropdownContainer = styled("div")`
  position: relative;
  display: inline-block;
`;

export const DropdownTrigger = styled("div")`
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  
  &:hover {
    color: var(--primary-color, #f9c0f550);
    
    svg {
      transform: rotate(180deg);
    }
  }
  
  /* Show dropdown on hover */
  ${DropdownContainer}:hover & {   
    svg {
      transform: rotate(180deg);
    }
  }
`;

export const DropdownMenu = styled("div")`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 9999; /* High z-index to ensure it appears above other elements */
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%) translateY(-10px);
  transition: all 0.3s ease;
  
  /* Show dropdown on parent hover */
  ${DropdownContainer}:hover & {
    opacity: 1;
    background:  #fff90;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
  
  /* Add a small arrow pointing up */
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid white;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -7px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 7px solid rgba(0, 0, 0, 0.1);
    z-index: -1;
  }
`;

export const DropdownItem = styled("div")`
  padding: 0;
  
  a {
    display: block;
    padding: 12px 16px;
    color: #18216d;
    text-decoration: none;
    font-size: 0.95rem;
    font-weight: 500;
    transition: all 0.2s ease;
    border-radius: 6px;
    margin: 4px;
    
    &:hover {
      text-decoration: none;
      transform: translateX(2px);
      color: var(--primary-color, #f9c0f5);
      text-underline-position: under;
      text-decoration: var(--primary-color, #f9c0f5) underline;
    }
    
    &:focus {
      color: var(--primary-color, #f9c0f5);
      text-underline-position: under;
      text-decoration: var(--primary-color, #f9c0f5) underline;
      outline-offset: 2px;
    }
  }
  
  /* First item */
  &:first-child a {
    margin-top: 8px;
  }
  
  /* Last item */
  &:last-child a {
    margin-bottom: 8px;
  }
`;

/* Additional utility to ensure no clipping */
export const HeaderWrapper = styled("div")`
  overflow: visible;
  position: relative;
`;